import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import axios from "axios";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOGLE_FALLBACK =
  "https://www.google.com/search?q=Ristorante+Milano+Piazza+Castello+41+Casale+Monferrato";

export default function Recensioni() {
  const { lang } = useLang();
  const [reviews, setReviews] = useState(null);
  const [googleUrl, setGoogleUrl] = useState(GOOGLE_FALLBACK);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((r) => setReviews(r.data))
      .catch(() => setReviews([]));
    axios
      .get(`${API}/settings`)
      .then((r) => {
        if (r.data?.google_review_url) setGoogleUrl(r.data.google_review_url);
      })
      .catch(() => {});
  }, []);

  if (!reviews || reviews.length === 0) return null;

  return (
    <section id="recensioni" data-testid="recensioni-section" className="py-24 lg:py-32 bg-[#171512] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(201,162,101,0.06),transparent_60%)]" />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="eyebrow">{lang === "it" ? "Dicono di Noi" : "Guest Words"}</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#FAF7F2]">
            {lang === "it" ? (
              <>Le voci di chi è <span className="italic text-[#C9A265]">tornato a trovarci</span></>
            ) : (
              <>Words from those who <span className="italic text-[#C9A265]">came back to us</span></>
            )}
          </h2>
          <div data-testid="google-rating-badge" className="mt-7 flex items-center justify-center gap-4">
            <span className="font-serif-display text-4xl text-[#FAF7F2]">{lang === "it" ? "4,7" : "4.7"}</span>
            <div className="flex gap-1">
              {[0, 1, 2, 3, 4].map((s) => (
                <span key={s} className="relative inline-block">
                  <Star size={22} className="text-[#3a342c]" />
                  <span
                    className="absolute inset-0 overflow-hidden"
                    style={{ width: s < 4 ? "100%" : "70%" }}
                  >
                    <Star size={22} className="fill-[#C9A265] text-[#C9A265]" />
                  </span>
                </span>
              ))}
            </div>
          </div>
          <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel">
            {lang === "it" ? "Voto medio su Google" : "Average rating on Google"}
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <motion.figure
              key={i}
              data-testid={`review-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className="bg-[#0D0C0A] border border-[rgba(201,162,101,0.18)] hover:border-[rgba(201,162,101,0.45)] rounded-2xl p-8 flex flex-col transition-colors duration-500"
            >
              <div className="flex gap-1" data-testid={`review-stars-${i}`}>
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star
                    key={s}
                    size={14}
                    className={s < (r.stars || 5) ? "fill-[#C9A265] text-[#C9A265]" : "text-[#3a342c]"}
                  />
                ))}
              </div>
              <blockquote className="mt-5 font-serif-display italic text-lg text-[#FAF7F2] leading-relaxed flex-1">
                "{r.text?.[lang] || r.text?.it}"
              </blockquote>
              <figcaption className="mt-6 text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel">
                {r.name}{r.source ? ` · ${r.source}` : ""}
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-12 text-center"
        >
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-review-button"
            className="inline-flex items-center gap-3 px-8 py-4 border border-[#C9A265] text-[#C9A265] text-xs uppercase tracking-[0.25em] font-cinzel rounded-full hover:bg-[#C9A265] hover:text-[#0D0C0A] transition-colors duration-300"
          >
            <Star size={15} className="fill-current" />
            {lang === "it" ? "Lasciaci una recensione su Google" : "Leave us a review on Google"}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
